import React, { useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import {
  AppBar, Box, Button, Divider, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem,
  Toolbar, useMediaQuery, useScrollTrigger, useTheme,
} from "@mui/material";

import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import LoginIcon from "@mui/icons-material/Login";
import MenuIcon from "@mui/icons-material/Menu";
import { NAVBAR_HEIGHT, navbarSurfaceSx } from "./common/layoutConstants";
import { BrandLogo } from "./common/BrandLogo";

type NavItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
  // Paths that should also light this item up (e.g. the admission form reached after verify).
  matches: (pathname: string) => boolean;
};

const NAV_ITEMS: NavItem[] = [
  { label: "Home", path: "/", icon: <HomeRoundedIcon />, matches: (p) => p === "/" },
  { label: "Enquiry", path: "/enquiry/create", icon: <EditNoteRoundedIcon />, matches: (p) => p.startsWith("/enquiry/create") },
  {
    label: "Admission",
    path: "/enquiry/verify",
    icon: <AssignmentTurnedInRoundedIcon />,
    matches: (p) => p.startsWith("/enquiry/verify") || p.startsWith("/admission"),
  },
];

export const PublicLayoutNavBar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // Bar sits flat on top of the page and gains a soft shadow once the user scrolls.
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 8 });

  const [mobileOpen, setMobileOpen] = useState(false);
  const [modulesAnchor, setModulesAnchor] = useState<null | HTMLElement>(null);

  const toggleDrawer = (open: boolean) => () => {
    setMobileOpen(open);
  };

  const openModulesMenu = (event: React.MouseEvent<HTMLElement>) => {
    setModulesAnchor(event.currentTarget);
  };

  const closeModulesMenu = () => {
    setModulesAnchor(null);
  };

  const go = (path: string) => {
    navigate(path);
    setMobileOpen(false);
    closeModulesMenu();
  };

  const isLoginPage = pathname.startsWith("/login");

  const brand = <BrandLogo to="/" onClick={() => setMobileOpen(false)} />;

  // ---------------- Mobile Drawer ----------------
  const drawerContent = (
    <Box sx={{ width: 280, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5 }}>
        {brand}
        <IconButton aria-label="Close menu" onClick={toggleDrawer(false)}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ px: 1, py: 1.5 }}>
        {NAV_ITEMS.map((item) => {
          const active = item.matches(pathname);
          return (
            <ListItem disablePadding key={item.path} sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={active}
                onClick={() => go(item.path)}
                sx={{
                  borderRadius: 2,
                  "&.Mui-selected": { bgcolor: "rgba(25, 118, 210, 0.08)", color: "primary.main" },
                  "&.Mui-selected:hover": { bgcolor: "rgba(25, 118, 210, 0.12)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: active ? "primary.main" : "text.secondary" }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} slotProps={{ primary: { fontWeight: active ? 600 : 500 } }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ mt: "auto", p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<LoginIcon />}
          onClick={() => go("/login")}
          sx={{ py: 1, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          Login
        </Button>
      </Box>
    </Box>
  );

  // ---------------- Desktop ----------------
  return (
    <AppBar
      position="sticky"
      elevation={0}
      color="inherit"
      sx={{
        ...navbarSurfaceSx,
        boxShadow: scrolled ? "0 4px 20px rgba(16, 24, 40, 0.06)" : "none",
        transition: "box-shadow 0.2s ease",
      }}
    >
      <Toolbar sx={{ height: NAVBAR_HEIGHT, maxWidth: 1400, width: "100%", mx: "auto", px: { xs: 2, md: 3 } }}>
        {brand}

        <Box sx={{ flexGrow: 1 }} />

        {/* ---------------- Desktop Menu ---------------- */}
        {!isMobile && (
          <Box component="nav" sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {NAV_ITEMS.map((item) => {
              const active = item.matches(pathname);
              return (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  aria-current={active ? "page" : undefined}
                  sx={{
                    ...navBtnStyle,
                    color: active ? "primary.main" : navBtnStyle.color,
                    fontWeight: active ? 600 : navBtnStyle.fontWeight,
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: 16,
                      right: 16,
                      bottom: 4,
                      height: 2,
                      borderRadius: 1,
                      bgcolor: "primary.main",
                      transform: active ? "scaleX(1)" : "scaleX(0)",
                      transition: "transform 0.2s ease",
                    },
                    "&:hover::after": { transform: "scaleX(1)" },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}

            <Button
              variant={isLoginPage ? "outlined" : "contained"}
              component={RouterLink}
              to="/login"
              startIcon={<LoginIcon />}
              sx={{
                ml: 2,
                borderRadius: 2,
                textTransform: "none",
                px: 2.5,
                py: 0.75,
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": { boxShadow: "0 4px 12px rgba(25, 118, 210, 0.25)" },
              }}
            >
              Login
            </Button>
          </Box>
        )}

        {/* ---------------- Mobile Menu Button ---------------- */}
        {isMobile && (
          <>
            <IconButton aria-label="Open menu" edge="end" onClick={toggleDrawer(true)} sx={{ color: "#111" }}>
              <MenuIcon />
            </IconButton>
            <Drawer anchor="right" open={mobileOpen} onClose={toggleDrawer(false)}>
              {drawerContent}
            </Drawer>
          </>
        )}

        {/* ---------------- Modules Dropdown ---------------- */}
        <Menu
          anchorEl={modulesAnchor}
          open={Boolean(modulesAnchor)}
          onClose={closeModulesMenu}
        >
          <MenuItem onClick={() => go("/enquiry/create")}>Enquiry & Admissions</MenuItem>
          <MenuItem onClick={() => go("/students")}>Students & Promotions</MenuItem>
          <MenuItem onClick={() => go("/attendance")}>Attendance & Reports</MenuItem>
          <MenuItem onClick={() => go("/fees")}>Fees & Payments</MenuItem>
          <MenuItem onClick={() => go("/exams")}>Exams & Reports</MenuItem>
          <MenuItem onClick={() => go("/communication")}>Communication</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

// ---------------- Styles ----------------
const navBtnStyle = {
  position: "relative",
  color: "#2c2c2c",
  fontWeight: 500,
  textTransform: "none",
  fontSize: "15px",
  px: 2,
  borderRadius: 2,
  "&:hover": {
    backgroundColor: "rgba(25, 118, 210, 0.04)",
  },
} as const;
